// Generated from scripts/bottom-navigation.ts. Rebuild with pnpm scripts:build.
import { Component } from '@theme/component';
import { getScrollEventTarget, getScrollTop, scrollContainerMediaQuery } from '@theme/scroll-container';
/** The class on `html` that the edge stacking contract reads (styles/edge-stack.css) to count the bar as hidden. */
const HIDDEN_CLASS = 'edge-bottom-nav-hidden';
/** Scrolling down this far in one direction hides the bar; a finger's tremor and an inertial bounce stay below it. */
const HIDE_DISTANCE = 16;
/** Scrolling up this far in one direction shows it again; shorter than the hide distance, so the way back is quick. */
const SHOW_DISTANCE = 8;
/** Within this distance of the top of the page the bar always shows. */
const TOP_ZONE = 64;
/** The kinds of item the bar holds, as `data-bottom-navigation-item` names them (snippets/bottom-navigation-item.liquid). */
const ITEM_TYPES = ['home', 'menu', 'search', 'account', 'cart', 'wishlist', 'link'];
function itemType(value) {
    return ITEM_TYPES.find((type) => type === value);
}
/**
 * Published on the bar, and so on the document, once per press of an item. It names the kind of item and nothing
 * else: an analytics bridge listens for it.
 */
export class BottomNavigationUseEvent extends CustomEvent {
    static eventName = 'bottom-navigation:use';
    constructor(item) {
        super(BottomNavigationUseEvent.eventName, { bubbles: true, detail: { item } });
    }
}
/**
 * The phone bottom navigation.
 *
 * Liquid renders the bar, its items and its place in the edge stacking contract; the bar works without this
 * module. The module adds two things for the shopper, one for the merchant and one for listeners:
 *
 * - With `data-hide-on-scroll="true"`, scrolling down hides the bar and scrolling up shows it. The state is one
 *   class on `html`, `edge-bottom-nav-hidden`; the contract counts the bar as zero height then, and the bar and
 *   every surface above it follow with their own transform transitions. The bar also shows within the top of the
 *   page and while a drawer or dialog holds the scroll lock.
 * - Extending `Component` registers the declarative `on:click` handlers that the search, menu and cart items use,
 *   whichever module loads first.
 * - In the theme editor, selecting a block shows the bar and marks the block's item, so the merchant can see it.
 * - A click on the link or button of an item publishes `bottom-navigation:use` with the item's kind, once
 *   (see `BottomNavigationUseEvent`).
 * - A press anywhere in the account item outside Shopify's own account button presses that button, so the whole
 *   item opens the account sheet; the button itself sits in the place of the icon.
 *
 * Instance-owned: the scroll listener, the scroll-lock observer and the pending frame are released on disconnect,
 * and the class on `html` is removed with them.
 */
class WontioBottomNavigation extends Component {
    #lifetime;
    #scrollTarget;
    #lockObserver;
    #frame = 0;
    #lastTop = 0;
    /** Signed distance scrolled since the direction last changed; positive is down. */
    #travel = 0;
    connectedCallback() {
        super.connectedCallback();
        this.#lifetime = new AbortController();
        const { signal } = this.#lifetime;
        this.addEventListener('shopify:block:select', this.#onBlockSelect, { signal });
        this.addEventListener('shopify:block:deselect', this.#onBlockDeselect, { signal });
        this.addEventListener('click', this.#onClick, { signal });
        if (this.dataset.hideOnScroll !== 'true')
            return;
        this.#lastTop = getScrollTop();
        scrollContainerMediaQuery.addEventListener('change', this.#bindScroll, { signal });
        this.#lockObserver = new MutationObserver(this.#onScrollLockChange);
        this.#lockObserver.observe(document.documentElement, { attributes: true, attributeFilter: ['scroll-lock'] });
        this.#bindScroll();
    }
    disconnectedCallback() {
        super.disconnectedCallback();
        this.#lifetime?.abort();
        this.#lifetime = undefined;
        this.#lockObserver?.disconnect();
        this.#lockObserver = undefined;
        this.#scrollTarget = undefined;
        cancelAnimationFrame(this.#frame);
        this.#frame = 0;
        this.#toggle(false);
    }
    /** Listens to whichever element scrolls the page now; the target changes when the layout switches. */
    #bindScroll = () => {
        this.#scrollTarget?.removeEventListener('scroll', this.#onScroll);
        this.#scrollTarget = getScrollEventTarget();
        this.#scrollTarget.addEventListener('scroll', this.#onScroll, { passive: true, signal: this.#lifetime?.signal });
    };
    #onScroll = () => {
        if (this.#frame)
            return;
        this.#frame = requestAnimationFrame(this.#update);
    };
    #update = () => {
        this.#frame = 0;
        const top = Math.max(0, getScrollTop());
        const delta = top - this.#lastTop;
        this.#lastTop = top;
        if (this.#isLocked() || top <= TOP_ZONE) {
            this.#travel = 0;
            this.#toggle(false);
            return;
        }
        if (delta === 0)
            return;
        this.#travel = Math.sign(delta) === Math.sign(this.#travel) ? this.#travel + delta : delta;
        if (this.#travel >= HIDE_DISTANCE)
            this.#toggle(true);
        else if (this.#travel <= -SHOW_DISTANCE)
            this.#toggle(false);
    };
    /** A drawer or dialog took or released the page scroll: show the bar, and count scrolling from here. */
    #onScrollLockChange = () => {
        this.#travel = 0;
        this.#lastTop = Math.max(0, getScrollTop());
        if (this.#isLocked())
            this.#toggle(false);
    };
    #isLocked() {
        return document.documentElement.hasAttribute('scroll-lock');
    }
    #toggle(hidden) {
        document.documentElement.classList.toggle(HIDDEN_CLASS, hidden);
    }
    /** A press on the control of an item. The editor's "Hidden" placeholder has no control, so it never counts. */
    #onClick = (event) => {
        if (!(event.target instanceof Element))
            return;
        const item = event.target.closest('[data-bottom-navigation-item]');
        const kind = itemType(item?.dataset.bottomNavigationItem);
        if (!item || !kind)
            return;
        if (kind === 'account') {
            // The press reaches here retargeted to the host; its path shows whether it landed on Shopify's button.
            // Shopify draws the button inside the open shadow root: a link to the account page for legacy accounts, a button otherwise.
            const button = item.querySelector('shopify-account')?.shadowRoot?.querySelector('.account-button');
            if (!button)
                return;
            if (event.composedPath().includes(button))
                this.dispatchEvent(new BottomNavigationUseEvent(kind));
            else
                button.click();
            return;
        }
        const control = event.target.closest('a, button');
        if (!control || !item.contains(control))
            return;
        this.dispatchEvent(new BottomNavigationUseEvent(kind));
    };
    #onBlockSelect = (event) => {
        this.#toggle(false);
        if (event.target instanceof Element)
            event.target.setAttribute('data-editor-selected', '');
    };
    #onBlockDeselect = (event) => {
        if (event.target instanceof Element)
            event.target.removeAttribute('data-editor-selected');
    };
}
if (!customElements.get('wontio-bottom-navigation')) {
    customElements.define('wontio-bottom-navigation', WontioBottomNavigation);
}
